import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
	getKeys,
	updateKey as updateKeyApi,
    removeKey as removeKeyApi,
    addKey as addKeyApi
} from '@/api/api-key'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'

interface IApiKeyState {
	keys: IApiKey[]
	loading: boolean
	error: string | null
	pagination?: IPagination

	updating: boolean
}

export interface IApiKey {
	_id: string
    name: string
    api_key: string
    description: string
    is_active: boolean
    expire_time?: Date
    updated_at?: Date
    updated_by?: string
    created_at: Date
    created_by: string
}

const initialState: IApiKeyState = {
	loading: false,
	keys: [],
	error: null,

	updating: false,
}

export const fetchKeys = createAsyncThunk<
	IApiResponse<IList<IApiKey>>,
	any
>('apikey/fetch_keys', async (params: any) => {
	return await getKeys(params)
})

export const updateKey = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('apikey/update_key', async ({ params, cb }: any) => {
	try {
		const result = await updateKeyApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error: any) {
		if (typeof cb === 'function') {
			cb({ success: false, message: error.message })
		}

		throw error
	}
})

export const removeKey = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('apikey/remove_key', async ({ params, cb }: any) => {
	try {
		const result = await removeKeyApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const addKey = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('apikey/add_key', async ({ params, cb }: any) => {
	try {
		const result = await addKeyApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error: any) {
		if (typeof cb === 'function') {
			cb({ success: false, message: error.message })
		}

		throw error
	}
})

const apiKeySlice = createSlice({
	name: 'proxy',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchKeys.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchKeys.fulfilled, (state, { payload }) => {
				state.loading = false
				state.pagination = payload.data!.pagination
				if (payload.success) {
					state.keys = payload.data!.records
				} else {
					state.keys = []
				}
			})
			.addCase(fetchKeys.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(updateKey.pending, (state) => {
				state.updating = true
			})
			.addCase(updateKey.fulfilled, (state) => {
				state.updating = false
			})
			.addCase(updateKey.rejected, (state, action) => {
				state.updating = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default apiKeySlice.reducer
